import { useEffect, useMemo, useState } from "react";
import { db, collection, onSnapshot } from "./boardData";

// Normalize the various alarm timestamp shapes to milliseconds.
function toMillis(v) {
  if (!v) return 0;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v; // seconds vs ms
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}

function isActive(a, now) {
  const ts = toMillis(a.TimeStamp || a.Eventtime || a.EventTime || a.postTime);
  const withinHour = ts && now - ts <= 60 * 60 * 1000;
  if (a.AlarmStatus === "true" || a.AlarmStatus === true) return true;
  if (typeof a.callcard === "string" && a.callcard.includes("DISPATCH") && withinHour) return true;
  return !!withinHour;
}

// Subscribes to BOTH alarm collection shapes (flat "{agency} Alarms" and nested
// Agencies/{agency}/Alarms) and returns the currently-active alarms, newest first.
export function useAlarms(agency) {
  const [flat, setFlat] = useState([]);
  const [nested, setNested] = useState([]);

  useEffect(() => {
    if (!agency) return undefined;
    const unsubs = [];
    try {
      unsubs.push(
        onSnapshot(
          collection(db, `${agency} Alarms`),
          (snap) => setFlat(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          () => setFlat([])
        )
      );
    } catch (e) { /* ignore */ }
    try {
      unsubs.push(
        onSnapshot(
          collection(db, "Agencies", agency, "Alarms"),
          (snap) => setNested(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          () => setNested([])
        )
      );
    } catch (e) { /* ignore */ }
    return () => unsubs.forEach((u) => u && u());
  }, [agency]);

  return useMemo(() => {
    const now = Date.now();
    const threeHoursAgo = now - 3 * 60 * 60 * 1000;
    const byId = new Map();
    [...flat, ...nested].forEach((a) => byId.set(a.id, a));
    return Array.from(byId.values())
      .filter((a) => {
        const ts = toMillis(a.TimeStamp || a.Eventtime || a.EventTime || a.postTime);
        return (!ts || ts >= threeHoursAgo) && isActive(a, now);
      })
      .sort(
        (a, b) =>
          toMillis(b.TimeStamp || b.Eventtime || b.postTime) -
          toMillis(a.TimeStamp || a.Eventtime || a.postTime)
      );
  }, [flat, nested]);
}
